import React, { useState, useEffect } from 'react';
import { productsAPI, ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './ProductsPage.css';

function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCart = (productId, quantity) => {
    setCart({ ...cart, [productId]: quantity });
  };

  const handleOrder = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const items = Object.entries(cart)
      .filter(([_, qty]) => qty > 0)
      .map(([productId, quantity]) => ({
        product_id: parseInt(productId),
        quantity: quantity,
      }));

    if (items.length === 0) {
      alert('Please add items to cart');
      return;
    }

    try {
      await ordersAPI.create({ items });
      alert('Order placed successfully!');
      setCart({});
      navigate('/orders');
    } catch (error) {
      alert('Error placing order: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // Check if it's already a full URL (S3)
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // Strip the /static/product_images/ prefix if it exists
    let cleanPath = imagePath;
    if (cleanPath.startsWith('/static/product_images/')) {
      cleanPath = cleanPath.replace('/static/product_images/', '');
    }

    // Use API URL from environment
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    return `${API_URL}/api/products/images/${cleanPath}`;
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page products-page">
      <h1 className="page-title">Products</h1>

      <div className="products-grid">
        {products.map((product) => (
          <div key={product.id} className="product-card">
            {product.image_path && (
              <div className="product-image">
                <img src={getImageUrl(product.image_path)} alt={product.name} />
              </div>
            )}
            <div className="product-info">
              <h3>{product.name}</h3>
              {(product.brand || product.weight_lbs) && (
                <p className="product-brand">
                  {product.brand}
                  {product.brand && product.weight_lbs && ' - '}
                  {product.weight_lbs && `${product.weight_lbs} lbs`}
                </p>
              )}
              <p className="product-description">{product.description}</p>
              <p className="product-price">${product.price_per_bag} / bag</p>

              <div className="quantity-selector">
                <label>Quantity:</label>
                <input
                  type="number"
                  min="0"
                  value={cart[product.id] || 0}
                  onChange={(e) => updateCart(product.id, parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {Object.values(cart).some(qty => qty > 0) && (
        <div className="cart-summary">
          <h3>Cart Summary</h3>
          {products.map((product) => {
            const qty = cart[product.id] || 0;
            if (qty === 0) return null;
            return (
              <div key={product.id} className="cart-item">
                {product.name} x {qty} = ${(product.price_per_bag * qty).toFixed(2)}
              </div>
            );
          })}
          <div className="cart-total">
            <strong>
              Total: $
              {products
                .reduce((total, p) => total + (cart[p.id] || 0) * p.price_per_bag, 0)
                .toFixed(2)}
            </strong>
          </div>
          <button onClick={handleOrder} className="btn btn-success">
            Place Order
          </button>
        </div>
      )}
    </div>
  );
}

export default ProductsPage;
